import React, { Component, ErrorInfo, ReactNode } from 'react'

import * as S from './PermissionErrorBoundary.styles'

// Simple input sanitization to prevent XSS
const sanitizeInput = (input: string): string => {
    return input.replace(/[<>'"&]/g, (char) => {
        const entities: Record<string, string> = {
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#x27;',
            '&': '&amp;',
        }
        return entities[char] || char
    })
}

interface Props {
    children: ReactNode
    fallback?: ReactNode
}

interface State {
    hasError: boolean
    error?: Error
}

export class PermissionErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = { hasError: false }
    }

    static getDerivedStateFromError(error: Error): State {
        // Update state so the next render will show the fallback UI
        return { hasError: true, error }
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Sanitize error message to prevent any potential XSS
        const sanitizedErrorMessage = sanitizeInput(error.message)

        // Log the error for monitoring (but only log sanitized data)
        // eslint-disable-next-line no-console
        console.error('Permission system error:', {
            message: sanitizedErrorMessage,
            stack: error.stack?.substring(0, 500), // Limit stack trace length
            componentStack: errorInfo.componentStack?.substring(0, 500),
        })

        // In production, send sanitized error data to monitoring service
        if (process.env.NODE_ENV === 'production') {
            // Example: Sentry.captureException(error, {
            //     contexts: {
            //         errorInfo: {
            //             ...errorInfo,
            //             componentStack: errorInfo.componentStack?.substring(0, 500)
            //         }
            //     },
            //     extra: { sanitizedMessage: sanitizedErrorMessage }
            // })
        }
    }

    render() {
        if (this.state.hasError) {
            // You can render any custom fallback UI
            if (this.props.fallback) {
                return this.props.fallback
            }

            return (
                <S.ErrorContainer>
                    <S.ErrorIcon>⚠️</S.ErrorIcon>
                    <S.ErrorMessage>
                        Permission check failed. Please refresh the page or contact support if the issue persists.
                    </S.ErrorMessage>
                </S.ErrorContainer>
            )
        }

        return this.props.children
    }
}
